import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import { ChangeTracker, ChangeType } from '../types/Cache/MutationSnapshot'
import { MutationSnapshot } from '../types/Cache/MutationSnapshot'
import Dexie from 'dexie'
import dayjs from 'dayjs'


/**
 * Helper function to track entity updates in the MutationSnapshot
 */
export async function trackEntityUpdate<T>(
	db: ForemanDB,
	entityType: keyof any,
	entity: T,
	userId = 1, // Default to 1, should be passed from context
): Promise<void> {
	try {
		// Update the mutation snapshot
		await db.mutationSnapshot
			.where('userId')
			.equals(userId)
			.modify((snapshot: any) => {
				const entityArray = snapshot[entityType]
				if (!Array.isArray(entityArray)) {
					console.warn(`Entity type ${String(entityType)} is not an array in snapshot`)
					return
				}

				// Get the appropriate primary key for the entity type
				const getEntityKey = (entity: any, entityType: string) => {
					switch (entityType) {
						case 'attendance':
							return entity.workerId
						case 'pallets':
							return entity.palletId
						case 'palletItems':
							return `${entity.palletItemId}-${entity.itemType}`
						case 'timesheets':
						case 'timeRecords':
						case 'dailyActivities':
						case 'workItemAttachments':
							return entity.id
						case 'updatedAttachments':
							return entity.attachmentId
						default:
							return entity.id
					}
				}

				const entityKey = getEntityKey(entity, String(entityType))
				const existingIndex = entityArray.findIndex((e: any) => {
					const existingKey = getEntityKey(e.entity, String(entityType))
					return existingKey === entityKey
				})

				const changeEntry = {
					entity: entity,
					changeType: ChangeType.UPDATED,
					timestamp: new Date(),
					uid: crypto.randomUUID(),
					logId: db.getLastLogId(),
				}

				if (existingIndex >= 0) {
					// If the existing entity is INSERTED, keep it as INSERTED
					if (entityArray[existingIndex].changeType === ChangeType.INSERTED) {
						changeEntry.changeType = ChangeType.INSERTED
					}
					entityArray[existingIndex] = changeEntry
				} else {
					entityArray.push(changeEntry)
				}
			})
	} catch (error) {
		console.error(`Error tracking entity update for ${String(entityType)}:`, error)
	}
}

/**
 * Helper function to track entity insertions in the MutationSnapshot
 */
export async function trackEntityInsert<T>(
	db: ForemanDB,
	entityType: keyof MutationSnapshot,
	entity: T,
	userId = 1,
): Promise<void> {
	try {
		await db.mutationSnapshot
			.where('userId')
			.equals(userId)
			.modify((snapshot: any) => {
				const entityArray = snapshot[entityType]
				if (!Array.isArray(entityArray)) {
					return
				}

				entityArray.push({
					entity: entity,
					changeType: ChangeType.INSERTED,
					timestamp: new Date(),
					uid: crypto.randomUUID(),
					logId: db.getLastLogId(),
				})
			})
	} catch (error) {
		console.error(`Error tracking entity insert for ${String(entityType)}:`, error)
	}
}

/**
 * Helper function to track entity deletions in the MutationSnapshot
 */
export async function trackEntityDelete<T>(
	db: ForemanDB,
	entityType: keyof any,
	entity: T,
	userId = 1,
): Promise<void> {
	try {
		await db.mutationSnapshot
			.where('userId')
			.equals(userId)
			.modify((snapshot: any) => {
				const entityArray = snapshot[entityType]
				if (!Array.isArray(entityArray)) {
					return
				}

				entityArray.push({
					entity: entity,
					changeType: ChangeType.DELETED,
					timestamp: new Date(),
					uid: crypto.randomUUID(),
					logId: db.getLastLogId(),
				})
			})
	} catch (error) {
		console.error(`Error tracking entity delete for ${String(entityType)}:`, error)
	}
}

/**
 * Helper function to track assignment changes (workItemAssignments, taskAssignments, reworkAssignments)
 */
export async function trackAssignmentChange(
	db: ForemanDB,
	assignmentType: 'workItemAssignments' | 'taskAssignments' | 'reworkAssignments',
	assignment: any,
	changeType: ChangeType,
	userId = 1,
): Promise<void> {
	try {
		await db.mutationSnapshot
			.where('userId')
			.equals(userId)
			.modify((snapshot: any) => {
				const assignmentArray = snapshot[assignmentType]
				if (!Array.isArray(assignmentArray)) {
					return
				}

				// For assignments, we need to find by composite key
				let existingIndex = -1
				if (assignmentType === 'workItemAssignments') {
					existingIndex = assignmentArray.findIndex(
						(a: any) =>
							a.entity.workerId === assignment.workerId &&
							a.entity.workItemId === assignment.workItemId,
					)
				} else if (assignmentType === 'taskAssignments') {
					existingIndex = assignmentArray.findIndex(
						(a: any) =>
							a.entity.workerId === assignment.workerId &&
							a.entity.workItemTaskId === assignment.workItemTaskId,
					)
				} else if (assignmentType === 'reworkAssignments') {
					existingIndex = assignmentArray.findIndex(
						(a: any) =>
							a.entity.workerId === assignment.workerId &&
							a.entity.reworkId === assignment.reworkId,
					)
				}
				// Smart cancellation logic: if inserting and delete exists, remove delete (and vice versa)
				if (existingIndex >= 0) {
					const existingChangeType = assignmentArray[existingIndex].changeType

					// If trying to insert but delete exists, remove the delete record
					if (changeType === ChangeType.INSERTED && existingChangeType === ChangeType.DELETED) {
						assignmentArray.splice(existingIndex, 1)
						return
					}

					// If trying to delete but insert exists, remove the insert record
					if (changeType === ChangeType.DELETED && existingChangeType === ChangeType.INSERTED) {
						assignmentArray.splice(existingIndex, 1)
						return
					}

					// For updates or same change type, replace the existing record
					assignmentArray[existingIndex] = {
						entity: assignment,
						changeType,
						timestamp: new Date(),
						uid: crypto.randomUUID(),
						logId: db.getLastLogId(),
					}
				} else {
					// No existing record, add new one
					assignmentArray.push({
						entity: assignment,
						changeType,
						timestamp: new Date(),
						uid: crypto.randomUUID(),
						logId: db.getLastLogId(),
					})
				}
			})
	} catch (error) {
		console.error(`Error tracking assignment change for ${assignmentType}:`, error)
	}
}

export async function trackTimeDelete(
	db: ForemanDB,
	collection: 'timeRecords' | 'timesheets',
	entity: any,
	userId = 1,
) {
	try {
		await db.mutationSnapshot
			.where('userId')
			.equals(userId)
			.modify((snapshot: any) => {
				const entityArray = snapshot[collection]
				if (!Array.isArray(entityArray)) {
					return
				}
				const existingInsertIndex = entityArray.findIndex(
					(e: any) => e.changeType === ChangeType.INSERTED && e.entity.id === entity.id,
				)
				if (existingInsertIndex >= 0 || entity.id < 0) {
					entityArray.splice(existingInsertIndex, 1)
				} else {
					entityArray.push({
						entity: entity,
						changeType: ChangeType.DELETED,
						timestamp: new Date(),
						uid: crypto.randomUUID(),
						logId: db.getLastLogId(),
					})
				}
			})
	} catch (error) {
		console.error(`Error tracking time delete for ${collection} and ${entity.id}:`, error)
	}
}

/**
 * Helper function to ensure mutationSnapshot table is included in transactions
 */
export function getTransactionTables(
	db: ForemanDB,
	additionalTables: (keyof ForemanDB)[] = [],
): (keyof ForemanDB)[] {
	const baseTables: (keyof ForemanDB)[] = ['mutations', 'mutationSnapshot', ...additionalTables]

	return baseTables
}
